import { NUMERIC_SEPARATOR } from './constants';
import type { Product } from './types/common';
import type { HandleBothCommaAndDotParams } from './types/number';

export const removeAllCommas = (input: string): string => input.replace(/,/g, '');

/**
 * Returns a numeric price and replaces all COMMA with DOT
 * eg '125,876,454.5' => 12665876454.5
 *    '12121,151' => 12121.151
 */
export const priceToFloat = (price: string | number): number => {
  const regExp = /^[0-9]+(,+[0-9]+)+(\\.[0-9]+)$/;
  const newPrice = price.toString();
  return regExp.test(newPrice)
    ? parseFloat(removeAllCommas(newPrice))
    : parseFloat(newPrice.replace(NUMERIC_SEPARATOR.COMMA, NUMERIC_SEPARATOR.DOT));
};

export const convertProductsPriceToFloat = (products: Product[]): Product[] => {
  return products.map((product) => {
    const { price, ...rest } = product;
    if (!price) {
      return product;
    }
    return {
      price: priceToFloat(price),
      ...rest,
    };
  });
};

export const removeAllDots = (input: string): string => input.replace(/\./g, '');

export const extractThreeConsecutiveDigits = (input: string) => /^[0-9]{3}\b/.exec(input);

export const handleBothCommaAndDot = ({
  firstMatch,
  firstCommaIndex,
  firstDotIndex,
  lastCommaIndex,
  lastDotIndex,
}: HandleBothCommaAndDotParams) => {
  if (firstCommaIndex < firstDotIndex) {
    if (lastCommaIndex > lastDotIndex || lastDotIndex > firstDotIndex) {
      return NaN;
    }
    return Number(removeAllCommas(firstMatch));
  }

  if (lastCommaIndex < lastDotIndex || lastCommaIndex > firstCommaIndex) {
    return NaN;
  }
  return Number(removeAllDots(firstMatch).replace(NUMERIC_SEPARATOR.COMMA, NUMERIC_SEPARATOR.DOT));
};

export const handleOnlyDot = (
  firstMatch: string,
  firstDotIndex: number,
  lastDotIndex: number,
) => {
  const threeDigits = extractThreeConsecutiveDigits(firstMatch.substring(firstDotIndex + 1));
  if (lastDotIndex > firstDotIndex || threeDigits) {
    return Number(removeAllDots(firstMatch));
  }
  return Number(firstMatch);
};

export const handleOnlyComma = (
  firstMatch: string,
  firstCommaIndex: number,
  lastCommaIndex: number,
) => {
  const threeDigits = extractThreeConsecutiveDigits(firstMatch.substring(firstCommaIndex + 1));
  return Number((lastCommaIndex > firstCommaIndex || threeDigits)
    ? removeAllCommas(firstMatch)
    : firstMatch.replace(NUMERIC_SEPARATOR.COMMA, NUMERIC_SEPARATOR.DOT));
};

/**
 * The `parseFloatI18n` function is used to parse and convert a localized number string into a valid
 * JavaScript number. It handles different number string formats that may include commas or dots as decimal
 * separators, and it ensures that the number is correctly formatted for parsing.
 */
export const parseFloatI18n = (input: string) => {
  const ZERO_INDEX = 0;
  if (typeof input !== 'string' || input === '') {
    return typeof input === 'number' ? input : NaN;
  }

  const numericMatches = /^[0-9.,eE-\s]+$/.exec(input);

  if (!numericMatches) {
    return parseFloat(input);
  }

  const [ numericMatch ] = numericMatches;
  const firstMatch = numericMatch.replace(/\s+/g, '');

  const firstCommaIndex = firstMatch.indexOf(NUMERIC_SEPARATOR.COMMA);
  const firstDotIndex = firstMatch.indexOf(NUMERIC_SEPARATOR.DOT);
  const lastCommaIndex = firstMatch.lastIndexOf(NUMERIC_SEPARATOR.COMMA);
  const lastDotIndex = firstMatch.lastIndexOf(NUMERIC_SEPARATOR.DOT);

  if (firstCommaIndex > ZERO_INDEX && firstDotIndex > ZERO_INDEX) {
    return handleBothCommaAndDot({
      firstMatch,
      firstCommaIndex,
      firstDotIndex,
      lastCommaIndex,
      lastDotIndex,
    });
  }

  if (firstDotIndex > ZERO_INDEX && firstCommaIndex < ZERO_INDEX) {
    return handleOnlyDot(firstMatch, firstDotIndex, lastDotIndex);
  }

  if (firstCommaIndex > ZERO_INDEX && firstDotIndex < ZERO_INDEX) {
    return handleOnlyComma(firstMatch, firstCommaIndex, lastCommaIndex);
  }

  if (firstCommaIndex === ZERO_INDEX || firstDotIndex === ZERO_INDEX) {
    return NaN;
  }

  return Number(firstMatch);
};
